<template>
  <MainLayout scroll-toolbar use-stable-scroll-gutter :bg-color="'white'">
    <template #toolbar>
      <div v-if="!spyderPaywall.show"
      class="relative flex items-center justify-between px-5 py-3.5 border-b border-border-normal">
        <div class="flex items-center gap-3">
          <img src="../../assets/icons/spyder.png" class="w-8 h-8">
          <BaseText type="label" size="sm" class="text-text-muted">
            Spyder
          </BaseText>
        </div>

        <!-- Spyder Search Bar -->
        <div class="absolute flex top-1/2 transform -translate-y-1/2 pointer-events-none" style="left: 308px; right: 308px; z-index: 40001">
          <div class="relative flex-grow mx-auto min-w-0 h-8 pointer-events-auto" style="max-width: 600px">
            <SearchBar
              :text-search.sync="textSearch"
              brands-only
              placeholder="Search brands..."
              @submit="handleSearchSubmit"
            />
          </div>
        </div>

        <div class="flex items-center gap-3">
          <button class="flex items-center gap-1.5 pl-1 py-1 pr-3 rounded-full border border-border-normal"
          style="transition: background-color 150ms ease-in-out, border-color 150ms ease-in-out, opacity 100ms ease-in-out;"
          :class="[loadingBrands ? 'opacity-0' : 'opacity-100', { 'cursor-default': isAnnualPlan }]" 
          :disabled="isAnnualPlan" @click="showBuySpyderCreditsModal = true">
            <img v-if="spyderBrands.length > 0" src="../../assets/icons/spyder-token.png" class="w-5 h-5">
            <img v-else src="../../assets/icons/spyder-token-disabled.png" class="w-5 h-5">
            <BaseText type="label" size="sm" class="text-text-muted">
              {{ spyderBrands.length }}/{{ getSpyderLimit }} credits
            </BaseText>
          </button>
          <BaseButton primary icon-only @click="showModal = true">
            <PlusIcon stroke="white" />
          </BaseButton>
        </div>
      </div>
      <!-- Subview Tabs -->
      <div v-if="!spyderPaywall.show" class="flex gap-2 w-full px-5 py-2 border-b border-border-normal">
        <router-link :to="{ name: 'SpyderBrandsView' }" class="spyder-view-tab" :class="{ 'active': selectedTab === 'brands' }"
        @click.native="clearSearch">
          <SpyderIcon class="w-5 h-5" />
          Tracked Brands
        </router-link>
        <router-link :to="{ name: 'SpyderFeedView' }" class="spyder-view-tab" :class="{ 'active': selectedTab === 'feed' }">
          <CommunityFeedIcon class="w-5 h-5" />
          Feed
        </router-link>
      </div>
    </template>
    <template #content>
      <div class="bg-white" :class="spyderPaywall.show ? '' : 'mb-10'">
        <BaseLoadingLogo v-if="getSpyderSubscriptionsLoading || loadingBrands" />
        <!-- SPYDER PAYWALL VIEW -->
        <SpyderPaywallState v-else-if="!getSpyderSubscriptionsLoading && !loadingBrands && spyderPaywall.show" 
        class="-mt-4" :subscriptionType="spyderPaywall" />
        <!-- =============== SEARCH RESULTS VIEW =============== -->
        <SpyderSearchResults v-else-if="submittedSearch"
          :search-query="submittedSearch"
          :page.sync="searchPage"
          :subscribed-brands="spyderBrands"
          @back="clearSearch"
        />
        <!-- =============== LOAD FAILED VIEW =============== -->
        <SpyderEmptyState v-else-if="loadFailed" class="mt-24"
          title="Couldn't connect to server"
          description="We weren't able to load your Spyder brands. Please refresh the page or try again later."
          :show-brands-link="false"
        />
        <!-- =============== FIRST TIME WELCOME VIEW =============== -->
        <div v-else-if="!getSpyderSubscriptionsLoading && !loadingBrands && spyderBrands.length === 0">
          <SpyderBrandsWelcomeView @openAddSpyderModal="initialModalView = $event, showModal = true"
          @openTutorialModal="tutorialTimestamp = 21, showTutorialModal = true" />
        </div>
        <!-- =============== MAIN VIEW ================ -->
        <div v-else-if="!(spyderBrands.length === 0 && !getSpyderSubscriptionsLoading) && !loadingBrands"
        class="flex flex-col gap-5 w-full">
          <div v-for="(folder, index) in sortedFolders" :key="`folder-${index}`" :ref="`folder-${folder.id}`"
          class="rounded-xl border border-border-normal">
            <div class="folder-header-grid-template w-full py-2.5">
              <div class="flex items-center gap-3 py-1.5 ml-4">
                <FolderIcon class="text-text-subdued flex-shrink-0" />
                <BaseText type="label" size="sm" class="text-text-muted">
                  {{ folder.name }}
                </BaseText>
              </div>
              <div class="flex items-center gap-3 px-3">
                <div class="relative">
                  <div class="cursor-default" @mouseenter="hoverFolderBrandInfo(index, true)"
                  @mouseleave="hoverFolderBrandInfo(index, false)">
                    <BaseText type="body" size="sm" class="text-text-normal">
                      {{ folderBrandCounts[index] }} Spyder Brands
                    </BaseText>
                  </div>
                  <transition>
                    <div v-if="!folderExpanded[index] && folderBrandInfoHovered[index]"
                    class="brand-info-tooltip flex flex-col gap-1 p-1 bg-neutral-700 rounded-lg w-max shadow-md"
                    style="min-width: 130px; max-width: 250px">
                      <div v-for="(brand, brandIndex) in getBrandsInFolder(folder.id, last_changed).slice(0, 7)"
                        :key="`brand-tooltip-row-${brand.key}-${brandIndex}`" class="flex items-center gap-2 p-1">
                        <BrandAvatar :avatar-src="brand.avatar" :brand-name="brand.name" :width="24" :height="24"
                        dark style="border-radius: 5px" />
                        <BaseText type="label" size="sm" class="text-white truncate">
                          {{ brand.name }}
                        </BaseText>
                      </div>
                      <div v-if="folderBrandCounts[index] > 7" class="flex items-center pt-0.5 pb-1.5 pl-1.5 pr-1">
                        <BaseText type="body" size="sm" class="text-text-subdued">
                          +{{ folderBrandCounts[index] - 7 }} more
                        </BaseText>
                      </div>
                    </div>
                  </transition>
                </div>
              </div>
              <div class="flex items-center gap-2">
                <BaseText type="body" size="sm" class="text-neutral-400">
                  Created on
                </BaseText>
                <BaseText type="body" size="sm">
                  {{ new Date(folder.createdAt || defaultCreatedDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long', day: 'numeric'
                  }) }}
                </BaseText>
                <button v-if="folderBrandCounts[index] > 0" class="ml-auto mr-5 transition-transform duration-200"
                  :style="{ transform: folderExpanded[index] ? 'scaleY(-1)' : 'scaleY(1)' }"
                  @click="expandFolder(index)">
                  <ChevronIcon :width="24" :height="24" class-name="text-icon-normal" />
                </button>
              </div>
            </div>
            <div :ref="`brand-folder-content-${index}`"
            :style="{ height: folderExpanded[index] ? `${(folderBrandCounts[index] + (folderBrandCounts[index] > 0 ? 1 : 0)) * 44}px` : '0px' }"
            class="brand-folder-content flex flex-col gap-2 px-2 w-full">
              <!-- Brands table header -->
              <div v-if="folderBrandCounts[index] > 0" class="brand-grid-template rounded-lg bg-neutral-10">
                <div class="group pl-12 pr-4 py-2 cursor-pointer flex items-center gap-1"
                @click="setSort('name', folder.id)">
                  <BaseText type="body" size="sm" class="text-text-normal">
                    Name
                  </BaseText>
                  <div class="transform transition-transform duration-200 hidden group-hover:block" 
                  :style="{
                    transform: showDescIcon('name', folder.id) ? 'scale(1, -1)' : '',
                    display: sortType.folder == folder.id && sortType.key === 'name' ? 'block' : ''
                  }">
                    <UpArrowIcon class-name="text-white" />
                  </div>
                </div>
                <div class="last-checked-column px-3 py-2 cursor-pointer flex gap-1 items-center group"
                @click="setSort('last_checked', folder.id)">
                  <BaseText type="body" size="sm" class="text-text-normal">
                    Last checked
                  </BaseText>
                  <div class="transform transition-transform duration-200 hidden group-hover:block" 
                  :style="{
                    transform: showDescIcon('last_checked', folder.id) ? 'scale(1, -1)' : '',
                    display: sortType.folder == folder.id && sortType.key === 'last_checked' ? 'block' : ''
                  }">
                    <UpArrowIcon class-name="text-white" />
                  </div>
                </div>
                <div class="data-since-column px-3 py-2 cursor-pointer flex items-center gap-1 group"
                @click="setSort('data_since', folder.id)">
                  <BaseText type="body" size="sm" class="text-text-normal">
                    Data Since
                  </BaseText>
                  <div class="transform transition-transform duration-200 hidden group-hover:block" :style="{
                    transform: showDescIcon('data_since', folder.id) ? 'scale(1, -1)' : '',
                    display: sortType.folder == folder.id && sortType.key === 'data_since' ? 'block' : ''
                  }">
                    <UpArrowIcon class-name="text-white" />
                  </div>
                </div>
                <div class="px-3 py-2 cursor-pointer flex items-center gap-1 group"
                @click="setSort('ad_count', folder.id)">
                  <BaseText type="body" size="sm" class="text-text-normal">
                    Ads
                  </BaseText>
                  <div class="transform transition-transform duration-200 hidden group-hover:block" :style="{
                    transform: showDescIcon('ad_count', folder.id) ? 'scale(1, -1)' : '',
                    display: sortType.folder == folder.id && sortType.key === 'ad_count' ? 'block' : ''
                  }">
                    <UpArrowIcon class-name="text-white" />
                  </div>
                </div>
                <div class="px-3 py-2">
                  <BaseText type="body" size="sm" class="text-text-normal">
                    Type
                  </BaseText>
                </div>
              </div>
              <!-- Brands rows -->
              <div v-for="(brand, brandIndex) in getSortedBrandsInFolder(folder.id)"
              :key="`brand-${brand.key}-${brandIndex}`" class="w-full" :class="{ 'cursor-default': brand.pending }">
                <router-link :to="{ name: 'SpyderView', params: { id: brand.id } }"
                class="brand-grid-template pr-0.5 rounded-lg transition-colors hover:bg-neutral-10"
                :disabled="brand.pending" :event="brand.pending ? '' : 'click'"
                :class="[brand.pending ? 'cursor-default' : 'cursor-pointer', { 'bg-neutral-10': expandedBrandOptions === brand.key }]">
                  <div class="flex items-center gap-3 px-2 py-1">
                    <div class="w-7 h-7 rounded-md overflow-hidden flex-shrink-0">
                      <AnalyticsSpinnerIcon v-if="brand.pending && !brand.avatar" class="ml-2 mt-2" />
                      <BrandAvatar v-else :avatar-src="brand.avatar" :brand-name="brand.name" :width="28" :height="28"
                      class="rounded-md" />
                    </div>
                    <BaseText type="label" size="sm" class="truncate"
                    :class="brand.pending ? 'text-text-disabled' : 'text-text-muted'"
                    :style="brand.pending ? 'font-weight: 400;' : ''">
                      {{ brand.name || 'In Progress...' }}
                    </BaseText>
                    <div v-if="isNewlyAddedBrand(brand)" class="new-brand-tag">
                      <BaseText size="xs" type="label">
                        New
                      </BaseText>
                    </div>
                  </div>
                  <div class="last-checked-column px-3 py-2">
                    <AnalyticsSpinnerIcon v-if="brand.pending" />
                    <BaseText v-else-if="formatLastChecked(brand)" type="body" size="sm" class="text-text-subdued">
                      {{ formatLastChecked(brand) }} ago
                    </BaseText>
                    <BaseText v-else type="body" size="sm" class="text-text-subdued">
                      &mdash;
                    </BaseText>
                  </div>
                  <div class="data-since-column px-3 py-2">
                    <BaseText type="body" size="sm" class="text-text-subdued">
                      {{ formatDate(brand?.dataSince) }}
                    </BaseText>
                  </div>
                  <div class="ad-status-grid-template px-3 py-2">
                    <div class="flex items-center gap-2 whitespace-nowrap">
                      <div class="w-1.5 h-1.5 rounded-full flex-shrink-0" :style="{ backgroundColor: brand.analytics?.activeCount ? '#00A879' : '#DFE1E7' }" />
                      <BaseText type="body" size="sm">
                        <span v-if="brand.pending" class="skeleton inline-block h-3 w-4 rounded align-middle" />
                        <span v-else>{{ brand.analytics?.activeCount?.toLocaleString() || 0 }}</span>
                        <span class="text-text-normal"> Active</span>
                      </BaseText>
                    </div>
                    <div class="flex items-center gap-2 whitespace-nowrap">
                      <div class="w-1.5 h-1.5 rounded-full flex-shrink-0" style="background-color: #DFE1E7;" />
                      <BaseText type="body" size="sm">
                        <span v-if="brand.pending" class="skeleton inline-block h-3 w-4 rounded align-middle" />
                        <span v-else>{{ brand.analytics?.inactiveCount?.toLocaleString() || 0 }}</span>
                        <span class="text-text-normal"> Inactive</span>
                      </BaseText>
                    </div>
                  </div>
                  <div class="format-breakdown-grid-template px-3 py-2">
                    <div class="flex items-center gap-1.5">
                      <VideoFormatIcon :width="16" :height="16" :stroke="brand.analytics?.displayFormats?.video ? '#5E6678' : '#A4ABB8'" />
                      <BaseText type="body" size="sm">
                        <span v-if="brand.pending" class="skeleton inline-block h-3 w-4 rounded align-middle" />
                        <span v-else>{{ brand.analytics?.displayFormats?.video?.toLocaleString() || 0 }}</span>
                      </BaseText>
                    </div>
                    <div class="flex items-center gap-1.5">
                      <ImageFormatIcon :width="16" :height="16" :stroke="brand.analytics?.displayFormats?.image ? '#5E6678' : '#A4ABB8'" />
                      <BaseText type="body" size="sm">
                        <span v-if="brand.pending" class="skeleton inline-block h-3 w-4 rounded align-middle" />
                        <span v-else>{{ brand.analytics?.displayFormats?.image?.toLocaleString() || 0 }}</span>
                      </BaseText>
                    </div>
                    <div class="flex items-center gap-1.5">
                      <CarouselFormatIcon :width="16" :height="16" :stroke="brand.analytics?.displayFormats?.carousel ? '#5E6678' : '#A4ABB8'" />
                      <BaseText type="body" size="sm">
                        <span v-if="brand.pending" class="skeleton inline-block h-3 w-4 rounded align-middle" />
                        <span v-else>{{ brand.analytics?.displayFormats?.carousel?.toLocaleString() || 0 }}</span>
                      </BaseText>
                    </div>
                    <div class="flex items-center gap-1.5">
                      <DynamicFormatIcon :width="16" :height="16" :stroke="brand.analytics?.displayFormats?.dpa ? '#5E6678' : '#A4ABB8'" />
                      <BaseText type="body" size="sm">
                        <span v-if="brand.pending" class="skeleton inline-block h-3 w-4 rounded align-middle" />
                        <span v-else>{{ brand.analytics?.displayFormats?.dpa?.toLocaleString() || 0 }}</span>
                      </BaseText>
                    </div>
                    <div class="flex items-center gap-1.5">
                      <OtherFormatIcon :width="16" :height="16" :stroke="brand.analytics?.displayFormats?.other ? '#5E6678' : '#A4ABB8'" />
                      <BaseText type="body" size="sm">
                        <span v-if="brand.pending" class="skeleton inline-block h-3 w-4 rounded align-middle" />
                        <span v-else>{{ brand.analytics?.displayFormats?.other?.toLocaleString() || 0 }}</span>
                      </BaseText>
                    </div>
                  </div>
                  <div class="flex items-center">
                    <button v-on-clickaway="() => { expandedBrandOptions = null }"
                    class="relative brand-menu-button p-0.5 rounded-md z-20"
                    :class="expandedBrandOptions === brand.key ? 'active' : 'inactive'"
                    :disabled="loadingMenu === brand.key"
                    @click.stop.prevent="expandedBrandOptions = expandedBrandOptions === brand.key ? null : brand.key">
                      <BaseLoadingSpinnerCircle v-if="loadingMenu === brand.key" class="text-icon-normal"/>
                      <KebabMenuIcon v-else />
                      <transition name="option">
                        <div v-if="expandedBrandOptions === brand.key"
                        class="brand-options flex flex-col gap-0.5 p-1 rounded-lg bg-white">
                          <button class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors hover:bg-neutral-25"
                          @click.stop.prevent="expandedBrandOptions = null, showChangeBrandFolderModal(brand)">
                            <ChangeFolderIcon class="transition-colors text-icon-normal group-hover:text-icon-muted" />
                            <BaseText type="body" size="sm"
                              class="transition-colors text-text-normal group-hover:text-text-muted">
                              Move
                            </BaseText>
                          </button>
                          <button class="group flex items-center gap-1.5 px-2 py-1.5 rounded-md transition-colors hover:bg-neutral-25"
                          @click.stop.prevent="expandedBrandOptions = null, removeSpyderBrand(brand)">
                            <DeleteIcon class-name="transition-colors text-icon-normal group-hover:text-icon-muted" />
                            <BaseText type="body" size="sm" class="transition-colors text-text-normal group-hover:text-text-muted">
                              Remove
                            </BaseText>
                          </button>
                        </div>
                      </transition>
                    </button>
                  </div>
                </router-link>
              </div>
            </div>
          </div>
          <button class="flex items-center justify-center gap-2 w-full py-2 rounded-xl transition-colors hover:bg-neutral-25"
          @click="showFolderModal = true">
            <PlusIcon stroke="#5E6678" />
            <BaseText type="label" size="sm" class="text-text-normal">
              Create new folder
            </BaseText>
          </button>
        </div>

        <AddSpyderModal 
          v-if="showModal" 
          :initial-view="initialModalView"
          :page-id-to-add="modalPageIdToAdd"
          :initial-folder="modalInitialFolder"
          @spyderAdded="handleAddSpyder"
          @close="showModal = false, initialModalView = 'search'"
          @scrollFolder="handleScrollToNewBrands"
        />
        <BuySpyderCreditsModal 
          v-if="showBuySpyderCreditsModal && !isAnnualPlan"
          :current-sub-count="spyderBrands.length"
          :spyder-limit="getSpyderLimit"
          @close="showBuySpyderCreditsModal = false" 
        />
        <FolderModal 
          v-if="showFolderModal" 
          @close="showFolderModal = false" 
        />
        <ChangeFolderModal 
          v-if="showChangeFolderModal" 
          :item="changeFolderBrand" item-type="brand"
          @close="showChangeFolderModal = false, changeFolderBrand = null" 
          @onFolderMove="last_changed = +new Date()" 
        />
        <BaseTutorialModal 
          v-if="showTutorialModal"
          video-url="https://www.youtube.com/embed/zNj71_-IIVk?si=C5PRycUlLRkX86l8" 
          :timestamp="tutorialTimestamp"
          @close="showTutorialModal = false" 
        />
        <FixSpyderModal
          v-if="showSpyderFixModal"
          :spyder-limit="spyderLimit"
          @close="showSpyderFixModal = false"
        />
        <SpyderBrandModal 
          v-if="showBrandPreviewModal" 
          :page-id="modalPageIdToAdd"
          @close="showBrandPreviewModal = false, modalPageIdToAdd = ''" 
          @spyderAdded="handleAddSpyder" 
        />
      </div>
    </template>
  </mainlayout>
</template>

<script>
import { mapGetters, mapActions, mapMutations } from 'vuex'
import firebase from '../../api/config/FirebaseConfig'
import { memoize } from 'lodash'
import moment from 'moment'
import { mixin as clickaway } from 'vue-clickaway2'
import { getBrandsInFolder, getUnfolderedBrands } from '@/utils/spyder/brandFolderUtils'
import { normalizeBrandsForSubscriptionsList } from '@/utils/spyder/brandUtils'

// How often to re-check whether brands the backend is still processing have resolved. The backend
// resolves pending brands every 30s and retries stalled builds after 15 mins, so poll for up to 15 mins.
const PENDING_BRAND_POLL_MS = 10000
const PENDING_BRAND_POLL_MAX_ATTEMPTS = 90

const RELOAD_COUNT_KEY = 'spyderBrandsReloadCount'
const MAX_RELOADS = 2

// Query params mounted() acts on once (opening a modal over the brands list) and then clears.
// They take priority over ?search= and ?page=, which are dropped when any of them is present.
const ONE_TIME_QUERY_PARAMS = ['id', 'folderId', 'pageId', 'add']

// ?add= opens the add brand modal on one of its tabs
const ADD_MODAL_VIEWS = ['search', 'manual']

// A ?page= param as a page number, falling back to page 1 when it's missing or invalid
const readPageParam = (page) => {
  const pageNumber = Number(page)
  return Number.isInteger(pageNumber) && pageNumber > 1 ? pageNumber : 1
}

// Components
import MainLayout from '../../layouts/MainLayout.vue'
import FolderModal from '../../components/sidebar/FolderModal.vue'
import ChangeFolderModal from '../../components/globals/ChangeFolderModal.vue'
import AddSpyderModal from '../../components/spyderV2/AddSpyderModal.vue'
import BuySpyderCreditsModal from '../../components/spyderV2/BuySpyderCreditsModal.vue'
import SpyderPaywallState from "../../components/spyderV2/SpyderPaywallState.vue"
import FixSpyderModal from '../../components/spyderV2/FixSpyderModal.vue'
import SpyderBrandModal from '../../components/spyderV2/SpyderBrandModal.vue'
import SpyderBrandsWelcomeView from '../../components/spyderV2/SpyderBrandsWelcomeView.vue'
import SpyderEmptyState from '../../components/spyderV2/SpyderEmptyState.vue'
import SearchBar from '../../components/search/SearchBar.vue'
import SpyderSearchResults from '../../components/spyderV2/SpyderSearchResults.vue'
import BrandAvatar from '../../components/brands/BrandAvatar.vue'

// Icons
import FolderIcon from '../../components/globals/Icons/SpyderV2Icons/FolderIcon.vue'
import VideoFormatIcon from '../../components/globals/Icons/FilterIcons/VideoFormatIcon.vue'
import ImageFormatIcon from '../../components/globals/Icons/FilterIcons/ImageFormatIcon.vue'
import CarouselFormatIcon from '../../components/globals/Icons/FilterIcons/CarouselFormatIcon.vue'
import DynamicFormatIcon from '../../components/globals/Icons/FilterIcons/DynamicFormatIcon.vue'
import OtherFormatIcon from '../../components/globals/Icons/FilterIcons/OtherFormatIcon.vue'
import AnalyticsSpinnerIcon from '../../components/globals/Icons/SpyderV2Icons/AnalyticsSpinnerIcon.vue'
import ChevronIcon from '../../components/globals/Icons/ChevronIcon.vue'
import PlayIcon from '../../components/globals/Icons/PlayIcon.vue'
import PlusIcon from '../../components/globals/Icons/PlusIcon.vue'
import KebabMenuIcon from '../../components/globals/Icons/KebabMenuIcon.vue'
import ChangeFolderIcon from '../../components/globals/Icons/ChangeFolderIcon.vue'
import DeleteIcon from '../../components/globals/Icons/DeleteIcon.vue'
import UpArrowIcon from '../../components/globals/Icons/SearchIcons/UpArrowKeyIcon.vue'
import SpyderIcon from '../../components/globals/Icons/SidebarIcons/SpyderIcon.vue'
import CommunityFeedIcon from '../../components/globals/Icons/DiscoveryIcons/CommunityFeedIcon.vue'

export default {
  name: 'SpyderBrandsView',
  components: {
    AddSpyderModal,
    AnalyticsSpinnerIcon,
    BrandAvatar,
    BuySpyderCreditsModal,
    CarouselFormatIcon,
    ChangeFolderIcon,
    ChangeFolderModal,
    ChevronIcon,
    DeleteIcon,
    DynamicFormatIcon,
    CommunityFeedIcon,
    FolderIcon,
    FolderModal,
    ImageFormatIcon,
    KebabMenuIcon,
    MainLayout,
    OtherFormatIcon,
    PlayIcon,
    PlusIcon,
    SpyderBrandsWelcomeView,
    SpyderEmptyState,
    SpyderIcon,
    VideoFormatIcon,
    UpArrowIcon,
    SpyderPaywallState,
    FixSpyderModal,
    SpyderBrandModal,
    SearchBar,
    SpyderSearchResults,
  },
  mixins: [clickaway],
  data() {
    return {
      // Data States
      brands: [],
      subscriptions: [],
      brandAnalytics: [],
      loadingBrands: true,
      loadFailed: false, // Mount still failed after hitting the auto-reload cap
      
      // Folder/Brand UI Control States
      showFolderModal: false,
      folderExpanded: [],
      folderBrandInfoHovered: [],
      defaultCreatedDate: new Date(),
      loadingMenu: null, // (Brand options menu) - key of the brand that is loading

      // Brand Search States
      textSearch: '', // Live search bar input
      submittedSearch: '', // Last submitted search; shows the search results view when set. Mirrored to ?search=
      searchPage: 1, // Results page of the submitted search. Mirrored to ?page= (left out on page 1)

      // Add Spyder Modal States
      showModal: false,
      initialModalView: 'search',
      modalPageIdToAdd: '',
      modalInitialFolder: { id: null, name: 'Default Folder' },

      // Brand Subscription Management
      spyderLimit: 2,
      showBuySpyderCreditsModal: false,
      showSpyderFixModal: false,
      showBrandPreviewModal: false,
      showChangeFolderModal: false,
      expandedBrandOptions: null,
      changeFolderBrand: null,
      lastAddedBrands: [],

      // Tutorial Modal States
      showTutorialModal: false,
      tutorialTimestamp: 58,

      // Misc
      pendingBrandsInterval: null,
      pendingBrandsPollAttempts: 0,
      last_changed: +new Date(),
      sortType: {
        key: 'name',
        value: 'asc',
        folder: null
      }
    }
  },
  watch: {
    getFolders: { // Ensure all the folders have an expanded/hover state mapped in the array (+1 for default)
      immediate: true,
      handler() {
        this.folderExpanded = [...this.getFolders.map(() => true), true]
        this.folderBrandInfoHovered = [...this.getFolders.map(() => false), false]
        this.removeTransitionListeners()
        this.attachTransitionListeners()
      }
    },
    spyderBrands: { // Bust the getBrandsInFolder memo whenever the brand list changes
      immediate: true,
      handler() {
        this.last_changed = +new Date()
      }
    },
    getIsOverSpyderLimit: { // Tracking more brands than the plan allows -> make the user pick which to keep
      immediate: true,
      handler(isOver) {
        if (isOver) this.showSpyderFixModal = true
      }
    },
    submittedSearch() { // Keep the URL in step so a refresh reopens the same results
      this.updateSearchQueryParams()
    },
    searchPage() {
      this.updateSearchQueryParams()
    },
    '$route.query.search'() { // Router navigations that change only the query (back/forward) reuse this view
      this.applyRouteSearch()
    },
    '$route.query.page'() {
      this.applyRouteSearch()
    }
  },
  computed: {
    ...mapGetters('AdvertisementsModule', ['getAdsBeingSaved']),
    ...mapGetters('AuthModule', ['getUser', 'getTeam', 'getStripeCustomer', 'getUserSubscription', 'getTeamSubscription', 'getUserFreeTrialDate', 'isFreeTier', 'getTeamSubscriptionState', 'getSpyderLimit', 'getIsLegacy']),
    ...mapGetters('BoardsModule', ['getFolders', 'getInitialFoldersLoaded']),
    ...mapGetters('SpyderModule', ['getSpyderSubscriptions', 'getPendingSpyderBrands', 'getSpyderSubscriptionsLoading', 'getIsOverSpyderLimit']),
    // Subscribed and still-processing brands, flattened into the one shape the table renders
    spyderBrands() {
      return normalizeBrandsForSubscriptionsList(this.getSpyderSubscriptions, this.getPendingSpyderBrands)
    },
    isAnnualPlan() {
      return this.getUserSubscription?.interval === 'year'
    },
    spyderPaywall() {
      const userSubscription = this.getUserSubscription
      const isBasic = userSubscription.internalProductId === 'basic_plan'
      const isLegacy = this.getIsLegacy
      return {
        show: ((isBasic && this.getSpyderLimit === 0) || (isLegacy && this.getSpyderLimit === 0) || this.getSpyderLimit === 0),
        isLegacy,
        isBasic
      }
    },
    sortedFolders() {
      // We want empty folders (besides the default folder) to be at the bottom
      const nonEmptyFolders = this.getFolders.filter(folder => (folder?.spyderBrands?.length || 0) > 0)
      return [{ id: null, name: 'Default Folder' }, ...nonEmptyFolders]
    },
    hasSubscription() {
      // check if a week has passed since trial and if they're free tier
      if (this.getTeam && !['past_due', 'canceled'].includes(this.getTeamSubscriptionState)) {
        return true
      }

      return !this.isFreeTier
    },
    folderBrandCounts() {
      return [
        this.getBrandsInFolder(null, this.last_changed).length,
        ...this.sortedFolders.slice(1).map(folder => this.getBrandsInFolder(folder.id, this.last_changed).length || 0)
      ]
    },
    selectedTab () {
      // The search results view has no tab of its own
      if (this.submittedSearch) return null
      return this.$route.name === 'SpyderFeedView' ? 'feed' : 'brands'
    }
  },
  created() {
    // Reopen the search results (and page) from the ?search= and ?page= params (e.g. after a refresh)
    this.applyRouteSearch()
  },
  async mounted() {
    try {
      this.spyderLimit = this.getSpyderLimit;

      if (this.spyderPaywall.show) {
        this.loadingBrands = false;
        return;
      }
      // Wait for folders to load before fetching spyder subscriptions
      await this.waitForFolders()

      await this.fetchSpyderSubscriptions()
      sessionStorage.removeItem(RELOAD_COUNT_KEY)
    } catch (error) {
      this.spyderLimit = 2
      console.error(error)
      const reloadCount = Number(sessionStorage.getItem(RELOAD_COUNT_KEY)) || 0
      if (reloadCount < MAX_RELOADS) {
        sessionStorage.setItem(RELOAD_COUNT_KEY, reloadCount + 1)
        location.reload()
      } else {
        sessionStorage.removeItem(RELOAD_COUNT_KEY)
        this.loadFailed = true
        this.$showAlert({ type: 'error', message: 'Failed to connect to server. Please try again later.' })
      }
    }

    // Get the account's created date (used for the default folder's "Created on" date)
    const userCreated = firebase.auth().currentUser.metadata.creationTime
    this.defaultCreatedDate = new Date(userCreated)

    // Idk what these were being used for, but im adding a pageId query param that will preload the brand for the user to add
    if (this.$route.query.id) {
      this.initialModalView = 'manual'
      this.modalPageIdToAdd = this.$route.query.id
      this.showModal = true
      history.replaceState({}, null, this.$route.path) // Clear the query params
    } else if (this.$route.query.folderId) {
      const folderId = this.$route.query.folderId
      if (folderId !== 'default') this.modalInitialFolder = this.getFolders.find(folder => folder.id === folderId)
      this.showModal = true
      history.replaceState({}, null, this.$route.path) // Clear the query params
    } else if (this.$route.query.pageId) {
      this.showBrandPreviewModal = true
      this.modalPageIdToAdd = this.$route.query.pageId
      history.replaceState({}, null, this.$route.path) // Clear the query params
    } else if (this.$route.query.add) {
      if (ADD_MODAL_VIEWS.includes(this.$route.query.add)) {
        this.initialModalView = this.$route.query.add
        this.showModal = true
      }
      history.replaceState({}, null, this.$route.path) // Clear the query params
    }

    this.loadingBrands = false

    this.pollPendingBrands()
    this.attachTransitionListeners()
  },
  beforeDestroy() {
    this.clearPendingBrandsPoll()
    this.removeTransitionListeners()
  },
  beforeRouteLeave(to, from, next) {
    // ?search= and ?page= are set with replaceState, so the router (and `from` in SpyderView) never sees them.
    // Remember them for when SpyderView's back button has to push this view instead of going back
    const query = {}
    if (this.submittedSearch) query.search = this.submittedSearch
    if (this.submittedSearch && this.searchPage > 1) query.page = String(this.searchPage)
    this.SET_BRANDS_LIST_QUERY(query)
    next()
  },
  methods: {
    ...mapActions('SpyderModule', ['fetchSpyderSubscriptions', 'removeSpyderBrands']),
    ...mapMutations('SpyderModule', ['SET_BRANDS_LIST_QUERY']),
    ...mapActions('BoardsModule', ['fetchFolders']),
    ...mapActions('AuthModule', ['setUserSubscription']),

    // ============================================================================ \\
    // ======================== DATA FETCH/CRUD METHODS =========================== \\
    // ============================================================================ \\
    
    getBrandsInFolder: memoize(function (folderId) {
      let brands

      if (!folderId) {
        brands = getUnfolderedBrands(this.spyderBrands, this.getFolders)
      } else {
        const folder = this.getFolders.find(folder => folder.id === folderId)
        if (!folder?.spyderBrands) return []
        // Drop entries that no longer resolve to a brand (orphaned references)
        brands = getBrandsInFolder(this.spyderBrands, folder)
      }

      // Process the analytics data
      const explicitMediaTypeKeys = ['video', 'image', 'carousel', 'dpa']
      brands = brands.map(brand => {
        const { analytics } = brand
        if (!analytics) return brand
        const otherMediaTypesCount = Object.keys(analytics.display_formats)
          .filter(key => !explicitMediaTypeKeys.includes(key))
          .reduce((acc, key) => acc + analytics.display_formats[key], 0)
        return {
          ...brand,
          analytics: {
            activeCount: analytics.active_count,
            inactiveCount: analytics.inactive_count,
            displayFormats: {
              video: analytics.display_formats.video,
              image: analytics.display_formats.image,
              carousel: analytics.display_formats.carousel,
              dpa: analytics.display_formats.dpa,
              other: otherMediaTypesCount
            }
          }
        }
      })

      return brands
    }, (arg1, arg2) => {
      // Update memoized value based on folderId + last_changed
      return `${arg1}-${arg2}`
    }),
    async waitForFolders() {
      if (this.getInitialFoldersLoaded && this.getFolders.length > 0) {
        return true
      }

      return new Promise((resolve) => {
        let attempts = 0
        const maxAttempts = 10

        const interval = setInterval(() => {
          attempts++

          if (this.getInitialFoldersLoaded && this.getFolders.length > 0) {
            clearInterval(interval)
            resolve(true)
          } else if (attempts >= maxAttempts) {
            clearInterval(interval)
            resolve(false)
          }
        }, 100)
      })
    },
    // Brands the backend is still processing come back under `pending`, so the whole subscription
    // list is re-fetched on an interval until nothing is pending anymore.
    pollPendingBrands() {
      this.clearPendingBrandsPoll()
      if (!this.getPendingSpyderBrands.length) return

      this.pendingBrandsInterval = setInterval(async () => {
        this.pendingBrandsPollAttempts++
        const pendingCountBefore = this.getPendingSpyderBrands.length
        try {
          await this.fetchSpyderSubscriptions()
        } catch (error) {
          console.error(error)
          this.clearPendingBrandsPoll()
          return
        }
        // A resolved brand can leave `pending` a moment before it shows up in `subscribed` (search index lag),
        // so poll once more after any brand resolves
        const brandResolved = this.getPendingSpyderBrands.length < pendingCountBefore
        // The server swaps a resolved brand's domain / advertiser id folder entry for its brand id, so pick that up
        if (brandResolved) this.fetchFolders().catch(console.error)
        if ((!this.getPendingSpyderBrands.length && !brandResolved) || this.pendingBrandsPollAttempts >= PENDING_BRAND_POLL_MAX_ATTEMPTS) {
          this.clearPendingBrandsPoll()
        }
      }, PENDING_BRAND_POLL_MS)
    },
    clearPendingBrandsPoll() {
      if (this.pendingBrandsInterval) clearInterval(this.pendingBrandsInterval)
      this.pendingBrandsInterval = null
      this.pendingBrandsPollAttempts = 0
    },
    async handleAddSpyder() {
      this.last_changed = +new Date()
      this.pollPendingBrands()
    },
    // Unsubscribes from a built brand, or cancels a pending one
    async removeSpyderBrand(brand) {
      try {
        this.loadingMenu = brand.key

        const { notCanceled } = await this.removeSpyderBrands([brand])
        if (notCanceled.length) {
          // It finished building first, so it's a subscription now
          await this.fetchSpyderSubscriptions()
          this.$showAlert({
            type: 'info',
            message: `${brand.name || 'This brand'} finished processing before it could be removed. Remove it again to unsubscribe.`
          })
        }
        this.last_changed = +new Date()
      } catch (error) {
        console.error(error)
        this.$showAlert({
          type: 'error',
          message: 'Failed to remove brand from Spyder. See console for details.',
        })
      } finally {
        this.loadingMenu = null
      }
    },
    handleSearchSubmit() {
      const query = this.textSearch.trim()
      if (!query.length) {
        this.clearSearch()
        return
      }
      this.submittedSearch = query
      this.searchPage = 1
    },
    clearSearch() {
      this.textSearch = ''
      this.submittedSearch = ''
      this.searchPage = 1
    },
    // Shows the results (and page) for the URL's ?search= and ?page= params, or the brands list without a search
    applyRouteSearch() {
      const { search, page } = this.$route.query
      const hasOneTimeParam = ONE_TIME_QUERY_PARAMS.some(param => this.$route.query[param])
      const query = !hasOneTimeParam && typeof search === 'string' ? search.trim() : ''
      this.textSearch = query
      this.submittedSearch = query
      this.searchPage = query ? readPageParam(page) : 1
      // Rewrite the params as applied right away, dropping ignored ones (a ?page= without a search included)
      // and normalizing the page. The one-time params stay for mounted() to use and clear
      if (search || page) this.updateSearchQueryParams()
    },
    // Sets (or removes) ?search= and ?page= without touching other params.
    updateSearchQueryParams() {
      const params = new URLSearchParams(window.location.search)
      if (this.submittedSearch) params.set('search', this.submittedSearch)
      else params.delete('search')
      if (this.submittedSearch && this.searchPage > 1) params.set('page', this.searchPage)
      else params.delete('page')
      const queryString = params.toString()
      window.history.replaceState(window.history.state, '', queryString ? `${this.$route.path}?${queryString}` : this.$route.path)
    },

    // ============================================================================ \\
    // ============================ SORTING METHODS =============================== \\
    // ============================================================================ \\

    setSort(key, folderId) {
      const sameKey = this.sortType.key === key
      const sameFolder = this.sortType.folder === folderId
      const isAsc = this.sortType.value === 'asc'
      this.sortType = {
        key,
        value: (sameKey && sameFolder) ? (isAsc ? 'desc' : 'asc') : 'asc',
        folder: folderId
      }
    },
    getSortedBrandsInFolder(folderId) {
      // Copy before sorting — getBrandsInFolder is memoized and its array is shared
      const brands = [...this.getBrandsInFolder(folderId, this.last_changed)]
      return brands.sort((a, b) => this.getSortCompare(a, b, folderId))
    },
    getSortCompare(a, b, folderId) {
      const { key, value } = this.sortType
      const isAsc = value === 'asc'
      let comparison = 0

      if (key === 'name') {
        // Handle cases where name might be undefined
        const nameA = a[key] || ''
        const nameB = b[key] || ''
        comparison = nameA.localeCompare(nameB)
      }
      if (key === 'last_checked') {
        comparison = -1 * ((this.getLastCheckedSeconds(a) || 0) - (this.getLastCheckedSeconds(b) || 0))
      }
      if (key === 'data_since') {
        comparison = this.getCreatedAtMS(a) - this.getCreatedAtMS(b)
      }
      if (key === 'ad_count') {
        comparison = (a.analytics?.activeCount || 0) - (b.analytics?.activeCount || 0)
      }

      return this.sortType.folder === folderId ? isAsc ? comparison : -comparison : 0
    },
    closeSpyderModal() {
      this.showModal = false
    },
    expandFolder(index) {
      this.$refs[`brand-folder-content-${index}`][0].style.overflow = 'hidden'
      this.folderExpanded = this.folderExpanded.map((expanded, i) => i === index ? !expanded : expanded)
    },
    hoverFolderBrandInfo(index, isHovered) {
      this.folderBrandInfoHovered = this.folderBrandInfoHovered.map((hovered, i) => i === index ? isHovered : hovered)
    },
    formatTime(time) {
      if (!time || isNaN(time)) return null
      const updatedAgo = ((new Date().getTime() - (time * 1000)) / 3600000)
      if (updatedAgo < 1) return `${Math.floor(updatedAgo * 60)}m`
      if (updatedAgo <= 48) return `${Math.floor(updatedAgo)}h`
      return `${Math.floor(updatedAgo / 24)}d`
    },
    getLastCheckedSeconds(brand) {
      const lastChecked = brand?.lastChecked
      if (!lastChecked) return null
      const parsed = moment.utc(lastChecked)
      return parsed.isValid() ? parsed.unix() : null
    },
    formatLastChecked(brand) {
      return this.formatTime(this.getLastCheckedSeconds(brand))
    },
    getCreatedAtMS(brand) {
      const createdAt = new Date(brand?.createdAt).getTime()
      return isNaN(createdAt) ? 0 : createdAt
    },
    formatDate(date) {
      if (!date) return '—'
      const options = { year: 'numeric', month: 'long', day: 'numeric' }
      return new Date(date).toLocaleDateString('en-US', options)
    },
    showDescIcon(key, folderId) {
      return this.sortType.folder === folderId && this.sortType.key === key && this.sortType.value === 'desc'
    },
    showChangeBrandFolderModal(brand) {
      this.changeFolderBrand = brand
      this.showChangeFolderModal = true
    },
    attachTransitionListeners() {
      // Attach onTransitionEnd event listener to each collapsible folder
      this.$nextTick(() => {
        for (let i = 0; i <= this.getFolders.length; i++) {
          const folder = this.$refs?.[`brand-folder-content-${i}`]?.[0]
          if (!folder) continue
          folder.addEventListener('transitionend', () => {
            if (this.folderExpanded[i]) {
              folder.style.overflow = 'visible'
            }
          })
        }
      })
    },
    removeTransitionListeners() {
      // Remove onTransitionEnd event listener from each collapsible folder
      for (let i = 0; i <= this.getFolders.length; i++) {
        const folder = this.$refs?.[`brand-folder-content-${i}`]?.[0]
        if (!folder) continue
        folder.removeEventListener('transitionend', () => {
          if (this.folderExpanded[i]) {
            folder.style.overflow = 'visible'
          }
        })
      }
    },
    async handleScrollToNewBrands(folderId, brandKeys = []) {
      this.lastAddedBrands = [...brandKeys]
      this.$nextTick(() => {
        const folderElement = this.$refs[`folder-${folderId}`]?.[0]
        if (folderElement && brandKeys.length > 0) {
          folderElement.scrollIntoView({ behavior: 'smooth' })
        }
      })
    },
    // Newly added brands are tracked by their brand id, or by the advertiser id / domain they were added
    // with while pending. A pending brand keeps its 'New' tag once it resolves, since its advertiser id
    // or domain then shows up in the resolved brand's advertiserIds / domains
    isNewlyAddedBrand(brand) {
      return this.lastAddedBrands.some(key => brand.key === key || brand.advertiserIds.includes(key) || brand.domains.includes(key))
    }
  },
  metaInfo: {
    title: 'Foreplay.co | Spyder'
  }
}
</script>

<style scoped>
/* ============ BRANDS LIST GRID STYLES ============ */

.brand-grid-template {
  display: grid;
  grid-template-columns: 250px 146px 180px 300px 1fr 32px;
  align-items: center;
}
@media (max-width: 1640px) {
  .last-checked-column { display: none; }
  .brand-grid-template {
    grid-template-columns: 250px 180px 300px 1fr 32px;
  }
}
@media (max-width: 1500px) {
  .data-since-column { display: none; }
  .brand-grid-template {
    grid-template-columns: 250px 300px 1fr 32px;
  }
}

.folder-header-grid-template {
  display: grid;
  grid-template-columns: 220px 235px 1fr;
  align-items: center;
}
.ad-status-grid-template {
  display: grid;
  grid-template-columns: 120px 1fr;
  align-items: center;
}
.format-breakdown-grid-template {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  align-items: center;
}
.brand-info-tooltip {
  position: absolute;
  left: calc(100% + 6px);
  top: -9px;
}
.brand-folder-content {
  transition: height 200ms ease-in-out;
}
.brand-menu-button {
  transition: background-color 100ms ease-in-out, box-shadow 100ms ease-in-out;
}
.brand-menu-button.active {
  background-color: #ECEFF3;
}
.brand-menu-button.inactive:hover {
  background-color: white;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}
.brand-options {
  position: absolute;
  right: calc(100% + 4px);
  top: 50%;
  transform: translateY(-50%);
  width: 105px;
  z-index: 50;
  box-shadow: 0px 1px 2px 0px rgba(4, 26, 75, 0.13), 0px 0px 0px 1px rgba(0, 56, 108, 0.08);
}
.new-brand-tag {
  padding: 2px 4px;
  background-color: #FDF2F1;
  color: #AB2D27;
  border-radius: 4px;
}

/* Tab Styles */
.spyder-view-tab {
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.375rem 0.625rem;
  border-radius: 0.375rem;
  font-size: 14px;
  line-height: 20px;
  font-weight: 400;
  color: #5E6678; /* text-text-normal */
  background-color: transparent;
  cursor: pointer;
  user-select: none;
  transition: color 150ms ease-in-out, background-color 150ms ease-in-out;
  white-space: nowrap;
}
.spyder-view-tab:hover {
  color: #303546; /* text-text-muted */
  background-color: #F6F8FA; /* bg-neutral-25 */
}
.spyder-view-tab.active {
  color: #303546; /* text-text-muted */
  background-color: #F6F8FA; /* bg-neutral-25 */
  font-weight: 500;
}

/* ============ VUE <transition> STYLES ============ */

.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}

.option-enter-active, .option-leave-active {
  transition: opacity 100ms ease-in-out;
}
.option-enter-from, .option-enter, .option-leave-to {
  opacity: 0;
}
.option-enter-to, .option-leave-from {
  opacity: 1;
}
</style>
